import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import { Notification } from '@/schema'
import { DEFAULT_SEARCH_REQUEST_BODY } from '@/utils/constants'

import { notificationApi } from './services'
import {
  NotificationDrawerState,
  NotificationFilterType,
  NotificationStatus,
} from './types'

interface NotificationDrawerStore extends NotificationDrawerState {
  openDrawer: () => void
  closeDrawer: () => void
  toggleDrawer: () => void
  setActiveTab: (tab: NotificationStatus) => void
  setFilterType: (filterType: NotificationFilterType) => void
  toggleMute: () => void
  togglePin: () => void
  markAllAsRead: () => void
  fetchNotifications: (reset?: boolean) => Promise<void>
  loadMoreNotifications: () => Promise<void>
  markAsRead: (notificationId: string) => void
  markAsUnread: (notificationId: string) => void
  updateNotification: (notification: Notification) => void
  addNotification: (notification: Notification) => void
  reset: () => void
}

const initialState: NotificationDrawerState = {
  isOpen: false,
  activeTab: NotificationStatus.Unread,
  isMuted: false,
  isPinned: false,
  filterType: NotificationFilterType.All,
  notifications: [],
  isLoading: false,
  error: null,
  hasMore: false,
  currentOffset: 0,
  totalItems: 0,
}

export const useNotificationDrawerStore = create<NotificationDrawerStore>()(
  devtools(
    (set) => ({
      ...initialState,

      openDrawer: () => {
        set({ isOpen: true }, false, 'openDrawer')
      },

      closeDrawer: () => {
        set({ isOpen: false }, false, 'closeDrawer')
      },

      toggleDrawer: () => {
        set((state) => ({ isOpen: !state.isOpen }), false, 'toggleDrawer')
      },

      setActiveTab: (tab: NotificationStatus) => {
        set({ activeTab: tab }, false, 'setActiveTab')
      },

      setFilterType: (filterType: NotificationFilterType) => {
        set({ filterType }, false, 'setFilterType')
      },

      toggleMute: () => {
        set((state) => ({ isMuted: !state.isMuted }), false, 'toggleMute')
      },

      togglePin: () => {
        set((state) => ({ isPinned: !state.isPinned }), false, 'togglePin')
      },

      markAllAsRead: () => {
        set(
          (state) => ({
            notifications: state.notifications.map((n) => ({
              ...n,
              read_at: n.read_at || new Date().toISOString(),
            })),
            activeTab: NotificationStatus.Read,
          }),
          false,
          'markAllAsRead'
        )
      },

      fetchNotifications: async (reset = true) => {
        const state = useNotificationDrawerStore.getState()

        // Don't fetch if already loading
        if (state.isLoading) return

        set({ isLoading: true, error: null }, false, 'fetchNotifications:start')

        try {
          const offset = reset ? 0 : state.currentOffset

          // Build filter_by object with status and notification_type
          const filterBy: {
            notification_type?: string[]
            status?: NotificationStatus[]
          } = {}

          // Add status filter based on active tab
          if (state.activeTab === NotificationStatus.Unread) {
            filterBy.status = [NotificationStatus.Unread]
          } else if (state.activeTab === NotificationStatus.Read) {
            filterBy.status = [NotificationStatus.Read]
          }

          // Add notification_type filter only when not "All"
          if (state.filterType !== NotificationFilterType.All) {
            filterBy.notification_type = [state.filterType]
          }

          const response = await notificationApi.listNotifications({
            offset,
            limit: DEFAULT_SEARCH_REQUEST_BODY.limit ?? 20,
            sort_by: '-created_at',
            q: DEFAULT_SEARCH_REQUEST_BODY.q || undefined,
            filter_by: filterBy,
          })

          set(
            (state) => ({
              notifications: reset
                ? response.data
                : [...state.notifications, ...response.data],
              isLoading: false,
              hasMore: response.pagination?.has_next ?? false,
              currentOffset: offset + response.data.length,
              totalItems: response.pagination?.total_items ?? 0,
            }),
            false,
            'fetchNotifications:success'
          )
        } catch (error) {
          set(
            {
              isLoading: false,
              error:
                error instanceof Error
                  ? error.message
                  : 'Failed to fetch notifications',
            },
            false,
            'fetchNotifications:error'
          )
        }
      },

      loadMoreNotifications: async () => {
        const state = useNotificationDrawerStore.getState()
        if (!state.hasMore || state.isLoading) return
        await state.fetchNotifications(false)
      },

      markAsRead: (notificationId: string) => {
        set(
          (state) => ({
            notifications: state.notifications.map((n) =>
              n.id === notificationId
                ? { ...n, read_at: new Date().toISOString() }
                : n
            ),
          }),
          false,
          'markAsRead'
        )
      },

      markAsUnread: (notificationId: string) => {
        set(
          (state) => ({
            notifications: state.notifications.map((n) =>
              n.id === notificationId ? { ...n, read_at: null } : n
            ),
          }),
          false,
          'markAsUnread'
        )
      },

      updateNotification: (notification: Notification) => {
        set(
          (state) => ({
            notifications: state.notifications.map((n) =>
              n.id === notification.id ? notification : n
            ),
          }),
          false,
          'updateNotification'
        )
      },

      addNotification: (notification: Notification) => {
        set(
          (state) => ({
            notifications: [notification, ...state.notifications],
            totalItems: state.totalItems + 1,
          }),
          false,
          'addNotification'
        )
      },

      reset: () => {
        set(initialState, false, 'reset')
      },
    }),
    {
      name: 'NotificationDrawerStore',
    }
  )
)
